/**
 * FERP PDF Sticky Note Layer (DRR-079)
 *
 * Click-to-place sticky notes with Mantine Popover for editing.
 */

import { useState, useCallback, useRef, type MouseEvent } from 'react';
import { Box, Paper, Text, Popover, Textarea, Button, Group, ActionIcon } from '@mantine/core';
import { IconNote, IconTrash } from '@tabler/icons-react';
import { useAuth } from '@/auth';
import { usePdfSuite } from './PdfSuiteContext';
import type { PdfAnnotation } from './types';

interface StickyNoteLayerProps {
  page: number;
  scale: number;
  annotations: PdfAnnotation[];
}

const STICKY_COLORS = ['#FFF9C4', '#C8E6C9', '#BBDEFB', '#F8BBD0', '#FFE0B2'];

export function StickyNoteLayer({ page, scale, annotations }: StickyNoteLayerProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const { state, addAnnotation, removeAnnotation, updateAnnotation, selectAnnotation } =
    usePdfSuite();
  const { user } = useAuth();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (state.viewer.annotationMode !== 'sticky') return;
      const el = overlayRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      const colorIdx = annotations.filter((a) => a.type === 'sticky').length % STICKY_COLORS.length;
      const id = `pending_${Date.now()}`;

      addAnnotation({
        id,
        type: 'sticky',
        page,
        rect: { x: x - 0.03, y: y - 0.03, width: 0.12, height: 0.08 },
        content: '',
        stickyColor: STICKY_COLORS[colorIdx],
        createdBy: user?.id ?? 'unknown',
        createdAt: new Date().toISOString(),
      });

      setEditingId(id);
      setEditText('');
    },
    [state.viewer.annotationMode, page, annotations, addAnnotation, user?.id],
  );

  const handleSave = useCallback(
    (ann: PdfAnnotation) => {
      updateAnnotation({ ...ann, content: editText });
      setEditingId(null);
      setEditText('');
    },
    [editText, updateAnnotation],
  );

  const stickyAnnotations = annotations.filter((a) => a.type === 'sticky');

  return (
    <Box
      ref={overlayRef}
      onClick={handleClick}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: state.viewer.annotationMode === 'sticky' ? 'auto' : 'none',
        cursor: state.viewer.annotationMode === 'sticky' ? 'copy' : 'default',
        zIndex: 13,
      }}
    >
      {stickyAnnotations.map((ann) => (
        <Popover
          key={ann.id}
          opened={editingId === ann.id}
          onChange={(opened) => {
            if (!opened) {
              handleSave(ann);
            }
          }}
          position="right"
          withArrow
        >
          <Popover.Target>
            <Paper
              onClick={(e) => {
                e.stopPropagation();
                selectAnnotation(ann.id);
                setEditingId(ann.id);
                setEditText(ann.content);
              }}
              shadow="sm"
              style={{
                position: 'absolute',
                left: `${ann.rect.x * 100}%`,
                top: `${ann.rect.y * 100}%`,
                width: `${ann.rect.width * 100}%`,
                minWidth: 60,
                padding: 6,
                backgroundColor: ann.stickyColor ?? '#FFF9C4',
                border: '1px solid rgba(0,0,0,0.1)',
                cursor: 'pointer',
                pointerEvents: 'auto',
                zIndex: 14,
              }}
            >
              {ann.content ? (
                <Text size="xs" lineClamp={3}>
                  {ann.content}
                </Text>
              ) : (
                <IconNote size={16} style={{ opacity: 0.5 }} />
              )}
            </Paper>
          </Popover.Target>
          <Popover.Dropdown>
            <Textarea
              size="xs"
              placeholder="Add note..."
              value={editText}
              onChange={(e) => setEditText(e.currentTarget.value)}
              autosize
              minRows={2}
              maxRows={6}
              w={200}
            />
            <Group gap="xs" mt="xs" justify="space-between">
              <ActionIcon
                variant="subtle"
                color="red"
                size="xs"
                onClick={() => {
                  removeAnnotation(ann.id);
                  setEditingId(null);
                }}
              >
                <IconTrash size={12} />
              </ActionIcon>
              <Button size="xs" onClick={() => handleSave(ann)}>
                Save
              </Button>
            </Group>
          </Popover.Dropdown>
        </Popover>
      ))}
    </Box>
  );
}
